import {
    Button,
    Dropdown,
    IMenuItem,
    IconButton,
    RequestStatus,
    Table,
    TableCell,
    TableHeadCell,
    TableRow,
    Pagination,
    IPaginationProps,
} from '../Common';
import { LeaveRequestUserInfoIncluded, ONGOING_MEDICAL_LEAVE_END_DATE } from '../../commonTypes/acs';
import { convertIsoToDiffDaysWithoutWeekendsAndHolidays, convertIsoToMonthDayInterval } from '../../util/date/dateHelper';
import cc from 'classcat';
import { LeaveRequestStatus, LeaveRequestType } from '../../commonTypes/leaveRequest.types';
import { useResizeListener } from '../../hooks/windowEvents/useResizeListener';
import { UserWithPicture, RequestAccordion } from '../LeaveRequestPartials';
import { useTranslation } from '../../hooks/useTranslation';
import Linkify from 'linkify-react';
import { Export } from '../Export';
import { useState } from 'react';
import { useHttpGetAllLeaveRequestsByOrganizationIdForExport, useHttpGetHolidaysByBranchOnMount } from '../../hooks';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { AuthState } from '../../store/slices/auth.slice';
import { formatToMonthDay } from '../../util/date/dateFormater';

interface IRequestList {
    requests: LeaveRequestUserInfoIncluded[];
    paginationProps: IPaginationProps;
    approveRequest: (id: string) => void;
    rejectProps: IRejectProps;
    filterProps: IFilterProps;
    title: string;
}

interface IRejectProps {
    openRejectRequest: (id: string) => void;
    openRejectDetails: (reason: string) => void;
}

interface IFilterProps {
    selectedStatus: IMenuItem;
    setSelectedStatus: (item: IMenuItem) => void;
    filterItems: IMenuItem[];
}

const emptyCell = '';

const RequestList = ({ requests, paginationProps, approveRequest, filterProps, rejectProps, title }: IRequestList) => {
    const { t } = useTranslation();
    const { isMaxWidthLarge: mobileView } = useResizeListener();

    const [isExportVisible, setIsExportVisible] = useState(false);
    const [selectedMember, setSelectedMember] = useState<IMenuItem | undefined>(undefined);
    const [selectedPolicy, setSelectedPolicy] = useState<IMenuItem | undefined>(undefined);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [previousFilters, setPreviousFilters] = useState<{
        selectedMember: IMenuItem | undefined;
        selectedPolicy: IMenuItem | undefined;
        startDate: Date | null;
        endDate: Date | null;
        selectedStatus: IMenuItem;
    }>({
        selectedMember: undefined,
        selectedPolicy: undefined,
        startDate: null,
        endDate: null,
        selectedStatus: filterProps.selectedStatus,
    });
    const hasRequests = requests.length !== 0;

    const auth = useSelector<RootState, AuthState>(state => state.auth);
    const { data: holidays } = useHttpGetHolidaysByBranchOnMount(auth.value.branchId);

    const { exec, data: exportData } = useHttpGetAllLeaveRequestsByOrganizationIdForExport(auth.value.organizationId);

    const dropDownChange = (item: IMenuItem) => {
        filterProps.setSelectedStatus(item);
    };

    const approvedStatus: IMenuItem = {
        label: 'Approved',
        value: 'APPROVED',
    };

    const toggleExportVisibility = async () => {
        setIsExportVisible(prevState => {
            if (!prevState) {
                exec();
                setPreviousFilters({
                    selectedMember,
                    selectedPolicy,
                    startDate,
                    endDate,
                    selectedStatus: filterProps.selectedStatus,
                });
                filterProps.setSelectedStatus(approvedStatus);
                setSelectedMember(undefined);
                setSelectedPolicy(undefined);
                setStartDate(null);
                setEndDate(null);
            }
            return !prevState;
        });
    };

    const handleCancelExport = () => {
        setSelectedMember(previousFilters.selectedMember);
        setSelectedPolicy(previousFilters.selectedPolicy);
        setStartDate(previousFilters.startDate);
        setEndDate(previousFilters.endDate);
        filterProps.setSelectedStatus(previousFilters.selectedStatus);

        setIsExportVisible(false);
    };

    const setFilterDates = (dateFrom: Date | null, dateTo: Date | null) => {
        setStartDate(dateFrom);
        setEndDate(dateTo);
    };

    const filteredRequests = isExportVisible
        ? exportData.filter(req => {
              const byMember = selectedMember ? req.userSub === selectedMember.value : true;
              const byPolicy = selectedPolicy ? req.policyType === selectedPolicy.label : true;
              const byDate = startDate && endDate ? req.fromDate >= startDate.toISOString() && req.toDate <= endDate.toISOString() : true;

              const currentDate = new Date();
              const isInPast = req.toDate < currentDate.toISOString();

              const byStatus = req.status === 'APPROVED';

              return isInPast && byMember && byPolicy && byDate && byStatus;
          })
        : requests;

    return (
        <div className="request-list">
            <div className="request-list__header">
                <h4>{title}</h4>
                <div className="request-list__header-actions">
                    <Dropdown
                        className="request-list__dropdown"
                        menuItems={filterProps.filterItems}
                        onChange={dropDownChange}
                        selected={filterProps.selectedStatus}
                        iconUrl={mobileView ? '/svg/filter.svg' : undefined}
                    />
                    {title !== t('medicalLeaveRequest.medicalLeaveRequests') && (
                        <Button text="Export" variant="bordered" onClick={toggleExportVisibility}></Button>
                    )}
                </div>
            </div>
            {isExportVisible && (
                <Export
                    setSelectedMember={setSelectedMember}
                    setSelectedPolicy={setSelectedPolicy}
                    setFilterDates={setFilterDates}
                    filteredRequests={filteredRequests}
                    onCancel={handleCancelExport}
                />
            )}{' '}
            {mobileView ? (
                <div>
                    {filteredRequests.map(req => (
                        <RequestAccordion
                            key={req.id}
                            request={req}
                            variant="forOwner"
                            approveRequest={approveRequest}
                            rejectRequest={rejectProps.openRejectRequest}
                        />
                    ))}
                </div>
            ) : (
                <Table className="request-list__table">
                    <TableRow>
                        <TableHeadCell>{t('profileDetails.user')}</TableHeadCell>
                        <TableHeadCell>{t('leaveRequest.policy')}</TableHeadCell>
                        <TableHeadCell>{t('leaveRequest.period')}</TableHeadCell>
                        <TableHeadCell>{t('leaveRequest.capitalize_days')}</TableHeadCell>
                        <TableHeadCell>{t('common.description')}</TableHeadCell>
                        <TableHeadCell>{t('leaveRequest.status')}</TableHeadCell>
                        <TableHeadCell>{emptyCell}</TableHeadCell>
                    </TableRow>
                    {filteredRequests.map(req => (
                        <TableRow key={req.id}>
                            <TableCell className="request-list__user-cell">
                                <UserWithPicture
                                    firstName={req.givenName}
                                    lastName={req.familyName}
                                    imgUrl={req.imageUrl}
                                    email={req.email}
                                    profileUrl={`/profile/${req.userSub}`}
                                />
                            </TableCell>
                            <TableCell className="request-list__policy">{req.policyType}</TableCell>
                            <TableCell>
                                {req.policyType === LeaveRequestType.MEDICAL_LEAVE && req.toDate === ONGOING_MEDICAL_LEAVE_END_DATE
                                    ? `${formatToMonthDay(req.fromDate)} - ${ONGOING_MEDICAL_LEAVE_END_DATE}`
                                    : convertIsoToMonthDayInterval(req.fromDate, req.toDate)}
                            </TableCell>
                            <TableCell>
                                {req.policyType === LeaveRequestType.MEDICAL_LEAVE && req.toDate === ONGOING_MEDICAL_LEAVE_END_DATE
                                    ? '/'
                                    : `${convertIsoToDiffDaysWithoutWeekendsAndHolidays(
                                          req.fromDate,
                                          req.toDate,
                                          holidays,
                                          req.leaveDurationType
                                      )} ${t('leaveRequest.dayUnit')}`}
                            </TableCell>
                            <TableCell>{req.description ? <Linkify>{req.description}</Linkify> : t('leaveRequest.noDescription')}</TableCell>
                            <TableCell
                                className={cc({
                                    'request-list__pending': req.status === LeaveRequestStatus.PENDING,
                                    'request-list__no-border-right': req.status !== LeaveRequestStatus.PENDING,
                                })}
                            >
                                <RequestStatus
                                    status={req.status}
                                    reason={req.rejectReason}
                                    openDetails={rejectProps.openRejectDetails}
                                ></RequestStatus>
                            </TableCell>
                            {req.status === LeaveRequestStatus.PENDING ? (
                                <TableCell>
                                    <div className="dashboard-leave-requests__buttons">
                                        <IconButton
                                            className="dashboard-leave-requests__button"
                                            variant="outline"
                                            iconUrl="/svg/circle-check-green.svg"
                                            iconAlt="approve request"
                                            onClick={() => approveRequest(req.id)}
                                        />
                                        <IconButton
                                            className="dashboard-leave-requests__button"
                                            variant="outline"
                                            iconUrl="/svg/circle-cancel.svg"
                                            iconAlt="approve request"
                                            onClick={() => rejectProps.openRejectRequest(req.id)}
                                        />
                                    </div>
                                </TableCell>
                            ) : (
                                <TableCell>{emptyCell}</TableCell>
                            )}
                        </TableRow>
                    ))}
                </Table>
            )}
            {!hasRequests && (
                <div className="requested-days__empty-label">
                    {title === t('medicalLeaveRequest.medicalLeaveRequests')
                        ? t('medicalLeaveRequest.noMedicalLeaveRequests')
                        : t('leaveRequest.emptyRequests')}
                </div>
            )}
            {hasRequests && !isExportVisible && (
                <div className="request-list__footer">
                    <Pagination {...paginationProps} />
                </div>
            )}
        </div>
    );
};

export default RequestList;
